import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Icewall form-switch: a pill track with a sliding knob. A real button with
 * role="switch" so it toggles on Space/Enter and reads correctly to a screen
 * reader; the label and hint sit to its right and are part of the hit area.
 */
export default function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  switchRight = false,
  className,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
  /** Settings-row layout: the label on the left and the switch at the far right. */
  switchRight?: boolean
  className?: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3',
        switchRight && 'flex-row-reverse justify-between',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-6 w-[38px] shrink-0 rounded-full border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/10 focus-visible:ring-offset-2',
          checked ? 'border-primary bg-primary' : 'border-foreground/20 bg-foreground/20',
        )}
      >
        <span
          className={cn(
            'absolute top-px left-px size-5 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-[14px]',
          )}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
    </label>
  )
}
